import {
  createBigDecimal,
  plus,
  toStringBigDecimal,
} from '@0xtorch/big-decimal'
/**
 * 会計期間の最終 state を DB に保存し、action / actionSource の損益集計を更新する。
 * 呼び出し元が summary を渡せる場合は DB 再読込を省き、渡されない場合だけ既存の再集計 query にフォールバックする。
 */
import type { CrossActionBundle, Loan } from '@pkg/accounting'
import { divideArrayIntoChunks } from '@pkg/basic'
import { and, between, eq, inArray, sql } from 'drizzle-orm'
import type { SQL, SQLWrapper } from 'drizzle-orm'
import type { SQLiteInsertValue } from 'drizzle-orm/sqlite-core'
import {
  assetBalanceColumnCount,
  assetBorrowColumnCount,
  crossActionBundleColumnCount,
  crossActionBundleRelationColumnCount,
  loanBorrowColumnCount,
  loanBorrowWithDebtColumnCount,
  loanBorrowWithDebtPositionColumnCount,
  loanColumnCount,
  loanDepositColumnCount,
  loanDepositWithBondColumnCount,
  loanDepositWithBondPositionColumnCount,
  nftAcquiredTimestampColumnCount,
  sqliteMaxHostParameterCount,
} from '../constants'
import { parseCrossActionBundlesToInsertData } from '../parsers/crossActionBundle'
import { parseLoansToInsertData } from '../parsers/loan'
import type { PeriodPlTotals } from '../periodPl'
import {
  accountingTransactionTable,
  accountingTransactionTransferTable,
  actionTable,
  assetBalanceTable,
  assetBorrowTable,
  crossActionBundleRelationTable,
  crossActionBundleTable,
  loanBorrowTable,
  loanBorrowWithDebtPositionTable,
  loanBorrowWithDebtTable,
  loanDepositTable,
  loanDepositWithBondPositionTable,
  loanDepositWithBondTable,
  loanTable,
  nftAcquiredTimestampTable,
} from '../schema'
import type {
  AccountingPeriod,
  AssetBalances,
  AssetBorrows,
  DatabaseWithTransaction,
} from '../types'
import { createDecimalSortKey, getMaxInsertRowCount } from '../utils'
import { saveComputedPeriodPlSummary } from './periodPlSummary'

const zeroDecimalText = '0'
const zeroDecimalSortKey = createDecimalSortKey({ value: zeroDecimalText })

/**
 * `saveAccountingPeriodData` が action / actionSource 更新に使う集計済み summary。
 */
export type AccountingPeriodComputedSummary = {
  readonly actionIds: readonly string[]
  readonly hasInsufficientAmountTxActionIds: readonly string[]
  readonly hasUnknownPriceTxActionIds: readonly string[]
  readonly actionTotalPls: ReadonlyMap<string, string>
  readonly periodPlTotals?: PeriodPlTotals
}

type SaveAccountingPeriodDataProfiler = {
  measure<T>(
    name: string,
    fn: () => Promise<T>,
    metadata?: Record<string, unknown>,
  ): Promise<T>
}

const noopSaveAccountingPeriodDataProfiler: SaveAccountingPeriodDataProfiler = {
  measure: async (_name, fn) => {
    return await fn()
  },
}

const directActionUpdateChunkSize = 120

type ActionSummaryUpdateChunk = {
  readonly ids: readonly string[]
  readonly hasInsufficientAmountTxSql: SQL<unknown>
  readonly hasUnknownPriceTxSql: SQL<unknown>
  readonly totalPlSql: SQL<unknown>
  readonly totalPlSortKeySql: SQL<unknown>
}

/**
 * `CASE id WHEN ... THEN true ELSE null END` 形式の nullable boolean SQL を生成する。
 * @param params 列参照と true にする id 一覧を受け取る。
 */
const createNullableBooleanCaseSql = ({
  idSql,
  ids,
}: {
  readonly idSql: SQLWrapper
  readonly ids: readonly string[]
}): SQL<unknown> => {
  if (ids.length === 0) {
    return sql`null`
  }

  return sql`CASE ${idSql} ${sql.join(
    ids.map((id) => sql`WHEN ${id} THEN true`),
    sql.raw(' '),
  )} ELSE null END`
}

/**
 * 事前計算済み summary から action table を直接更新する chunk を生成する。
 * @param params action id 一覧と flag / total PL 集計を受け取る。
 */
const createActionSummaryUpdateChunks = ({
  actionIds,
  actionTotalPls,
  hasInsufficientAmountTxActionIds,
  hasUnknownPriceTxActionIds,
}: {
  readonly actionIds: readonly string[]
  readonly actionTotalPls: ReadonlyMap<string, string>
  readonly hasInsufficientAmountTxActionIds: readonly string[]
  readonly hasUnknownPriceTxActionIds: readonly string[]
}): readonly ActionSummaryUpdateChunk[] => {
  const hasInsufficientAmountTxActionIdSet = new Set(
    hasInsufficientAmountTxActionIds,
  )
  const hasUnknownPriceTxActionIdSet = new Set(hasUnknownPriceTxActionIds)

  return divideArrayIntoChunks(actionIds, directActionUpdateChunkSize).map(
    (chunk) => {
      const totalPlCaseBranches = sql.join(
        chunk.map(
          (id) =>
            sql`WHEN ${id} THEN ${actionTotalPls.get(id) ?? zeroDecimalText}`,
        ),
        sql.raw(' '),
      )
      const totalPlSortKeyCaseBranches = sql.join(
        chunk.map(
          (id) =>
            sql`WHEN ${id} THEN ${createDecimalSortKey({
              value: actionTotalPls.get(id) ?? zeroDecimalText,
            })}`,
        ),
        sql.raw(' '),
      )

      return {
        ids: chunk,
        hasInsufficientAmountTxSql: createNullableBooleanCaseSql({
          idSql: actionTable.id,
          ids: chunk.filter((id) => hasInsufficientAmountTxActionIdSet.has(id)),
        }),
        hasUnknownPriceTxSql: createNullableBooleanCaseSql({
          idSql: actionTable.id,
          ids: chunk.filter((id) => hasUnknownPriceTxActionIdSet.has(id)),
        }),
        totalPlSql: sql`CASE ${actionTable.id} ${totalPlCaseBranches} ELSE ${zeroDecimalText} END`,
        totalPlSortKeySql: sql`CASE ${actionTable.id} ${totalPlSortKeyCaseBranches} ELSE ${zeroDecimalSortKey} END`,
      }
    },
  )
}

type DecimalCaseUpdateChunk = {
  readonly ids: readonly string[]
  readonly totalPlSql: SQL<unknown>
  readonly totalPlSortKeySql: SQL<unknown>
}

/**
 * CASE 式で decimal 集約値を一括更新するための chunk を生成する。
 * @param params 更新対象エントリと列参照を受け取る。
 * @returns chunk ごとの id 一覧と CASE 式を返す。
 */
const createDecimalCaseUpdateChunks = ({
  entries,
  idSql,
  totalPlSql,
  totalPlSortKeySql,
  defaultTotalPlSql,
  defaultTotalPlSortKeySql,
}: {
  readonly entries: readonly (readonly [string, string])[]
  readonly idSql: SQLWrapper
  readonly totalPlSql: SQLWrapper
  readonly totalPlSortKeySql: SQLWrapper
  readonly defaultTotalPlSql?: SQLWrapper
  readonly defaultTotalPlSortKeySql?: SQLWrapper
}): readonly DecimalCaseUpdateChunk[] => {
  const chunkSize = Math.max(
    1,
    Math.floor((sqliteMaxHostParameterCount - 1) / 5),
  )

  return divideArrayIntoChunks(entries, chunkSize).map((chunk) => {
    const totalPlCaseBranches = sql.join(
      chunk.map(([id, value]) => sql`WHEN ${id} THEN ${value}`),
      sql.raw(' '),
    )
    const totalPlSortKeyCaseBranches = sql.join(
      chunk.map(
        ([id, value]) =>
          sql`WHEN ${id} THEN ${createDecimalSortKey({ value })}`,
      ),
      sql.raw(' '),
    )

    return {
      ids: chunk.map(([id]) => id),
      totalPlSql: sql`CASE ${idSql} ${totalPlCaseBranches} ELSE ${defaultTotalPlSql ?? totalPlSql} END`,
      totalPlSortKeySql: sql`CASE ${idSql} ${totalPlSortKeyCaseBranches} ELSE ${defaultTotalPlSortKeySql ?? totalPlSortKeySql} END`,
    }
  })
}

/**
 * 会計期間内 action の損益合計を計算する。
 * @param params database と会計期間を受け取る。
 * @returns action id 一覧と action 単位の損益合計を返す。
 */
const collectHistorySortTotals = async ({
  accountingPeriod,
  database,
}: {
  readonly accountingPeriod: AccountingPeriod
  readonly database: DatabaseWithTransaction['database']
}): Promise<
  Pick<AccountingPeriodComputedSummary, 'actionIds' | 'actionTotalPls'>
> => {
  const periodActionRows = await database
    .select({ actionId: actionTable.id })
    .from(actionTable)
    .where(
      between(
        actionTable.timestamp,
        new Date(accountingPeriod.start),
        new Date(accountingPeriod.end),
      ),
    )

  const transactionPlRows = await database
    .select({
      actionId: accountingTransactionTable.actionId,
      totalPl: accountingTransactionTransferTable.totalPl,
    })
    .from(accountingTransactionTable)
    .leftJoin(
      accountingTransactionTransferTable,
      eq(
        accountingTransactionTransferTable.transactionId,
        accountingTransactionTable.id,
      ),
    )
    .where(
      eq(accountingTransactionTable.accountingPeriodId, accountingPeriod.id),
    )

  const actionTotals = new Map<string, ReturnType<typeof createBigDecimal>>()
  for (const row of transactionPlRows) {
    if (row.totalPl === null) {
      continue
    }

    const totalPl = createBigDecimal(row.totalPl)
    actionTotals.set(
      row.actionId,
      plus(actionTotals.get(row.actionId) ?? createBigDecimal(0n), totalPl),
    )
  }

  return {
    actionIds: periodActionRows.map(({ actionId }) => actionId),
    actionTotalPls: new Map(
      [...actionTotals.entries()].map(([actionId, totalPl]) => [
        actionId,
        toStringBigDecimal(totalPl),
      ]),
    ),
  }
}

/**
 * DB に保存済み transaction 群から period summary を再構築する。
 * @param params 会計期間と database を受け取る。
 * @returns action flag と PL 集計をまとめた summary を返す。
 */
const collectAccountingPeriodComputedSummaryFromDatabase = async ({
  accountingPeriod,
  database,
}: {
  readonly accountingPeriod: AccountingPeriod
  readonly database: DatabaseWithTransaction['database']
}): Promise<AccountingPeriodComputedSummary> => {
  const [hasInsufficientAmountTxActionIds, hasUnknownPriceTxActionIds, totals] =
    await Promise.all([
      database
        .selectDistinct({ id: accountingTransactionTable.actionId })
        .from(accountingTransactionTable)
        .where(
          and(
            eq(
              accountingTransactionTable.accountingPeriodId,
              accountingPeriod.id,
            ),
            eq(accountingTransactionTable.category, 'insufficient'),
          ),
        ),
      database
        .selectDistinct({ id: accountingTransactionTable.actionId })
        .from(accountingTransactionTable)
        .leftJoin(
          accountingTransactionTransferTable,
          eq(
            accountingTransactionTable.id,
            accountingTransactionTransferTable.transactionId,
          ),
        )
        .where(
          and(
            eq(
              accountingTransactionTable.accountingPeriodId,
              accountingPeriod.id,
            ),
            eq(accountingTransactionTransferTable.zeroCompleted, true),
          ),
        ),
      collectHistorySortTotals({ accountingPeriod, database }),
    ])

  return {
    actionIds: totals.actionIds,
    hasInsufficientAmountTxActionIds: hasInsufficientAmountTxActionIds.map(
      ({ id }) => id,
    ),
    hasUnknownPriceTxActionIds: hasUnknownPriceTxActionIds.map(({ id }) => id),
    actionTotalPls: totals.actionTotalPls,
  }
}

type SaveAccountingPeriodDataParameters = {
  database: DatabaseWithTransaction
  accountingPeriod: AccountingPeriod
  loans: readonly Loan[]
  crossActionBundles: readonly CrossActionBundle[]
  assetBalances: readonly AssetBalances[]
  assetBorrows: readonly AssetBorrows[]
  nftAcquiredTimestamps: ReadonlyMap<string, number>
  computedSummary?: AccountingPeriodComputedSummary
  profiler?: SaveAccountingPeriodDataProfiler
}

/**
 * 会計期間の最終 state と action / source 集計を保存する。
 * @param params 保存対象データと、あれば事前計算済み summary を受け取る。
 */
export const saveAccountingPeriodData = async ({
  database: { database, transaction },
  accountingPeriod,
  loans,
  crossActionBundles,
  assetBalances,
  assetBorrows,
  nftAcquiredTimestamps,
  computedSummary,
  profiler,
}: SaveAccountingPeriodDataParameters) => {
  const effectiveProfiler = profiler ?? noopSaveAccountingPeriodDataProfiler
  const effectiveComputedSummary =
    computedSummary ??
    (await collectAccountingPeriodComputedSummaryFromDatabase({
      accountingPeriod,
      database,
    }))

  const {
    loan: loanSqls,
    loanBorrow: loanBorrowSqls,
    loanBorrowWithDebt: loanBorrowWithDebtSqls,
    loanBorrowWithDebtPosition: loanBorrowWithDebtPositionSqls,
    loanDeposit: loanDepositSqls,
    loanDepositWithBond: loanDepositWithBondSqls,
    loanDepositWithBondPosition: loanDepositWithBondPositionSqls,
  } = parseLoansToInsertData(accountingPeriod.id, loans)
  const {
    crossActionBundle: crossActionBundleSqls,
    crossActionBundleRelation: crossActionBundleRelationSqls,
  } = parseCrossActionBundlesToInsertData(
    accountingPeriod.id,
    crossActionBundles,
  )
  const assetBalanceSqls = assetBalances.flatMap((item) =>
    item.balances.map(
      (balance, order): SQLiteInsertValue<typeof assetBalanceTable> => ({
        accountingPeriodId: accountingPeriod.id,
        asset: item.asset,
        order,
        value: toStringBigDecimal(balance.value),
        amount: toStringBigDecimal(balance.amount),
      }),
    ),
  )
  const assetBorrowSqls = assetBorrows.flatMap((item) =>
    item.borrows.map(
      (borrow, order): SQLiteInsertValue<typeof assetBorrowTable> => ({
        accountingPeriodId: accountingPeriod.id,
        asset: item.asset,
        order,
        value: toStringBigDecimal(borrow.value),
        amount: toStringBigDecimal(borrow.amount),
        borrowedValue: toStringBigDecimal(borrow.borrowedValue),
        borrowedAmount: toStringBigDecimal(borrow.borrowedAmount),
      }),
    ),
  )
  const nftAcquiredTimestampSqls: SQLiteInsertValue<
    typeof nftAcquiredTimestampTable
  >[] = [...nftAcquiredTimestamps.entries()].map(([nftId, timestamp]) => ({
    accountingPeriodId: accountingPeriod.id,
    nftId,
    timestamp: new Date(timestamp),
  }))
  const isDirectSummarySync = computedSummary !== undefined
  if (isDirectSummarySync) {
    await effectiveProfiler.measure(
      'saveAccountingPeriodData.fast.total',
      async () =>
        await transaction([
          (tx) => [
            ...divideArrayIntoChunks(
              loanSqls,
              getMaxInsertRowCount(loanColumnCount),
            ).map((chunk) => tx.insert(loanTable).values([...chunk])),
            ...divideArrayIntoChunks(
              crossActionBundleSqls,
              getMaxInsertRowCount(crossActionBundleColumnCount),
            ).map((chunk) =>
              tx.insert(crossActionBundleTable).values([...chunk]),
            ),
            ...divideArrayIntoChunks(
              assetBalanceSqls,
              getMaxInsertRowCount(assetBalanceColumnCount),
            ).map((chunk) => tx.insert(assetBalanceTable).values([...chunk])),
            ...divideArrayIntoChunks(
              assetBorrowSqls,
              getMaxInsertRowCount(assetBorrowColumnCount),
            ).map((chunk) => tx.insert(assetBorrowTable).values([...chunk])),
            ...divideArrayIntoChunks(
              nftAcquiredTimestampSqls,
              getMaxInsertRowCount(nftAcquiredTimestampColumnCount),
            ).map((chunk) =>
              tx.insert(nftAcquiredTimestampTable).values([...chunk]),
            ),
          ],
          (tx) => [
            ...divideArrayIntoChunks(
              loanBorrowWithDebtPositionSqls,
              getMaxInsertRowCount(loanBorrowWithDebtPositionColumnCount + 1),
            ).map((chunk) =>
              tx.insert(loanBorrowWithDebtPositionTable).values([...chunk]),
            ),
            ...divideArrayIntoChunks(
              loanBorrowWithDebtSqls,
              getMaxInsertRowCount(loanBorrowWithDebtColumnCount + 1),
            ).map((chunk) =>
              tx.insert(loanBorrowWithDebtTable).values([...chunk]),
            ),
            ...divideArrayIntoChunks(
              loanBorrowSqls,
              getMaxInsertRowCount(loanBorrowColumnCount + 1),
            ).map((chunk) => tx.insert(loanBorrowTable).values([...chunk])),
            ...divideArrayIntoChunks(
              loanDepositWithBondPositionSqls,
              getMaxInsertRowCount(loanDepositWithBondPositionColumnCount + 1),
            ).map((chunk) =>
              tx.insert(loanDepositWithBondPositionTable).values([...chunk]),
            ),
            ...divideArrayIntoChunks(
              loanDepositWithBondSqls,
              getMaxInsertRowCount(loanDepositWithBondColumnCount + 1),
            ).map((chunk) =>
              tx.insert(loanDepositWithBondTable).values([...chunk]),
            ),
            ...divideArrayIntoChunks(
              loanDepositSqls,
              getMaxInsertRowCount(loanDepositColumnCount + 1),
            ).map((chunk) => tx.insert(loanDepositTable).values([...chunk])),
            ...divideArrayIntoChunks(
              crossActionBundleRelationSqls,
              getMaxInsertRowCount(crossActionBundleRelationColumnCount + 1),
            ).map((chunk) =>
              tx.insert(crossActionBundleRelationTable).values([...chunk]),
            ),
          ],
          (tx) => [
            (async () => {
              const insufficientIds = new Set(
                effectiveComputedSummary.hasInsufficientAmountTxActionIds,
              )
              const unknownPriceIds = new Set(
                effectiveComputedSummary.hasUnknownPriceTxActionIds,
              )
              // SQL と実行待ちクエリは一度に 120 件分だけ保持する。
              for (
                let offset = 0;
                offset < effectiveComputedSummary.actionIds.length;
                offset += directActionUpdateChunkSize
              ) {
                const ids = effectiveComputedSummary.actionIds.slice(
                  offset,
                  offset + directActionUpdateChunkSize,
                )
                const chunks = createActionSummaryUpdateChunks({
                  actionIds: ids,
                  actionTotalPls: effectiveComputedSummary.actionTotalPls,
                  hasInsufficientAmountTxActionIds: ids.filter((id) =>
                    insufficientIds.has(id),
                  ),
                  hasUnknownPriceTxActionIds: ids.filter((id) =>
                    unknownPriceIds.has(id),
                  ),
                })
                for (const chunk of chunks) {
                  await tx
                    .update(actionTable)
                    .set({
                      generatedTx: true,
                      hasInsufficientAmountTx: chunk.hasInsufficientAmountTxSql,
                      hasUnknownPriceTx: chunk.hasUnknownPriceTxSql,
                      totalPl: chunk.totalPlSql,
                      totalPlSortKey: chunk.totalPlSortKeySql,
                    })
                    .where(inArray(actionTable.id, [...chunk.ids]))
                }
              }
            })(),
          ],
          (tx) =>
            computedSummary?.periodPlTotals === undefined
              ? []
              : [
                  saveComputedPeriodPlSummary({
                    database: tx,
                    period: accountingPeriod,
                    totals: computedSummary.periodPlTotals,
                  }),
                ],
        ]),
      {
        actionCount: effectiveComputedSummary.actionIds.length,
      },
    )
    return
  }

  const actionTotalPlUpdateChunks = createDecimalCaseUpdateChunks({
    entries: [...effectiveComputedSummary.actionTotalPls.entries()],
    idSql: actionTable.id,
    totalPlSql: actionTable.totalPl,
    totalPlSortKeySql: actionTable.totalPlSortKey,
  })

  await transaction([
    (tx) => [
      // loan 保存
      ...divideArrayIntoChunks(
        loanSqls,
        getMaxInsertRowCount(loanColumnCount),
      ).map((chunk) => tx.insert(loanTable).values([...chunk])),
      // cross action bundle 保存
      ...divideArrayIntoChunks(
        crossActionBundleSqls,
        getMaxInsertRowCount(crossActionBundleColumnCount),
      ).map((chunk) => tx.insert(crossActionBundleTable).values([...chunk])),
      // asset balance 保存
      ...divideArrayIntoChunks(
        assetBalanceSqls,
        getMaxInsertRowCount(assetBalanceColumnCount),
      ).map((chunk) => tx.insert(assetBalanceTable).values([...chunk])),
      // asset borrow 保存
      ...divideArrayIntoChunks(
        assetBorrowSqls,
        getMaxInsertRowCount(assetBorrowColumnCount),
      ).map((chunk) => tx.insert(assetBorrowTable).values([...chunk])),
      // nft acquired timestamp 保存
      ...divideArrayIntoChunks(
        nftAcquiredTimestampSqls,
        getMaxInsertRowCount(nftAcquiredTimestampColumnCount),
      ).map((chunk) => tx.insert(nftAcquiredTimestampTable).values([...chunk])),
      // action generatedTx 更新
      tx
        .update(actionTable)
        .set({
          generatedTx: true,
          hasInsufficientAmountTx: null,
          hasUnknownPriceTx: null,
          totalPl: zeroDecimalText,
          totalPlSortKey: zeroDecimalSortKey,
        })
        .where(
          between(
            actionTable.timestamp,
            new Date(accountingPeriod.start),
            new Date(accountingPeriod.end),
          ),
        ),
    ],
    (tx) => [
      // loan 関連 table 保存
      ...divideArrayIntoChunks(
        loanBorrowWithDebtPositionSqls,
        getMaxInsertRowCount(loanBorrowWithDebtPositionColumnCount + 1),
      ).map((chunk) =>
        tx.insert(loanBorrowWithDebtPositionTable).values([...chunk]),
      ),
      ...divideArrayIntoChunks(
        loanBorrowWithDebtSqls,
        getMaxInsertRowCount(loanBorrowWithDebtColumnCount + 1),
      ).map((chunk) => tx.insert(loanBorrowWithDebtTable).values([...chunk])),
      ...divideArrayIntoChunks(
        loanBorrowSqls,
        getMaxInsertRowCount(loanBorrowColumnCount + 1),
      ).map((chunk) => tx.insert(loanBorrowTable).values([...chunk])),
      ...divideArrayIntoChunks(
        loanDepositWithBondPositionSqls,
        getMaxInsertRowCount(loanDepositWithBondPositionColumnCount + 1),
      ).map((chunk) =>
        tx.insert(loanDepositWithBondPositionTable).values([...chunk]),
      ),
      ...divideArrayIntoChunks(
        loanDepositWithBondSqls,
        getMaxInsertRowCount(loanDepositWithBondColumnCount + 1),
      ).map((chunk) => tx.insert(loanDepositWithBondTable).values([...chunk])),
      ...divideArrayIntoChunks(
        loanDepositSqls,
        getMaxInsertRowCount(loanDepositColumnCount + 1),
      ).map((chunk) => tx.insert(loanDepositTable).values([...chunk])),
      // cross action bundle relation 保存
      ...divideArrayIntoChunks(
        crossActionBundleRelationSqls,
        getMaxInsertRowCount(crossActionBundleRelationColumnCount + 1),
      ).map((chunk) =>
        tx.insert(crossActionBundleRelationTable).values([...chunk]),
      ),
      // action hasInsufficientAmountTx 更新
      ...divideArrayIntoChunks(
        effectiveComputedSummary.hasInsufficientAmountTxActionIds,
        sqliteMaxHostParameterCount - 1,
      ).map((chunk) =>
        tx
          .update(actionTable)
          .set({
            hasInsufficientAmountTx: true,
          })
          .where(inArray(actionTable.id, [...chunk])),
      ),
      ...actionTotalPlUpdateChunks.map((chunk) =>
        tx
          .update(actionTable)
          .set({
            totalPl: chunk.totalPlSql,
            totalPlSortKey: chunk.totalPlSortKeySql,
          })
          .where(inArray(actionTable.id, [...chunk.ids])),
      ),
    ],
    (tx) => [
      // action hasUnknownPriceTx 更新
      ...divideArrayIntoChunks(
        effectiveComputedSummary.hasUnknownPriceTxActionIds,
        sqliteMaxHostParameterCount - 1,
      ).map((chunk) =>
        tx
          .update(actionTable)
          .set({
            hasUnknownPriceTx: true,
          })
          .where(inArray(actionTable.id, [...chunk])),
      ),
    ],
  ])
}
